import { useState, useCallback, useRef } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { Button, Space, Alert, Checkbox, Spin } from 'antd';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Pagination } from 'antd';
import Empty from 'components/Empty';
import { useMount } from 'ahooks';
import {
  useClearAll,
  useCurrentItems,
  usePage,
  useSyncSelectedItems,
  useToggleCurrentPageChecked,
  useToggleAllChecked,
  useCurrentPageCheckboxState,
  useAllCheckboxState,
} from './stateForSellerSku';
import { useSameSellerSkuListData } from './useListData';
import { Item } from './ItemList';

type ImplementProps = {
  sellerSku: string;
  internalItemId: string;
};

function Implement(props: ImplementProps) {
  const { sellerSku, internalItemId } = props;
  const [fetch, loading] = useSameSellerSkuListData(sellerSku, internalItemId);
  const toggleCurrentPageChecked = useToggleCurrentPageChecked(sellerSku);
  const toggleAllChecked = useToggleAllChecked(sellerSku);
  const pageCheckboxStatus = useCurrentPageCheckboxState();
  const allCheckboxStatus = useAllCheckboxState();
  const [page] = usePage();
  useMount(() => {
    fetch();
  });
  const { t } = useTranslate();
  return (
    <Container>
      {page.count > 0 && (
        <Alert showIcon message={t('knowledge.item.sellerSku.alert', { x: page.count })} />
      )}
      <div className="items-select">
        {page.count > 0 && (
          <div className="checkboxs">
            <Checkbox
              onChange={(e) => toggleAllChecked(e.target.checked)}
              indeterminate={allCheckboxStatus === 'indeterminate'}
              checked={allCheckboxStatus === 'checked'}
            >
              {t('storeGroup.全选')}
            </Checkbox>
            <Checkbox
              onChange={(e) => toggleCurrentPageChecked(e.target.checked)}
              indeterminate={pageCheckboxStatus === 'indeterminate'}
              checked={pageCheckboxStatus === 'checked'}
            >
              {t('voucher.本页全选')}
            </Checkbox>
          </div>
        )}
        <div className="list-container">
          <div className="list">
            <ListView loading={loading} sellerSku={sellerSku}></ListView>
          </div>
          <div className="pagination">{page.count > 0 && <Page />}</div>
        </div>
      </div>
    </Container>
  );
}

type ListViewProps = {
  loading: boolean;
  sellerSku: string;
};

function ListView(props: ListViewProps) {
  const { loading, sellerSku } = props;
  const listData = useCurrentItems();
  if (loading) {
    return (
      <div className="spin-container">
        <Spin />
      </div>
    );
  }
  if (listData.length === 0) {
    return (
      <div className="spin-container">
        <Empty />
      </div>
    );
  }
  return (
    <>
      {listData.map((item) => (
        <Item key={item.internalItemId} item={item} sellerSku={sellerSku} mode="sellerSku" />
      ))}
    </>
  );
}

function Page() {
  const [state, change] = usePage();
  return (
    <Pagination
      size="small"
      total={state.count}
      current={state.pageNo}
      pageSize={state.pageSize}
      onChange={(page, _pageSize) => {
        change(page);
      }}
    />
  );
}

const useSellerSkuSelect = () => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslate();
  const sellerSkuRef = useRef('');
  const internalItemIdRef = useRef('');
  const clearAll = useClearAll();
  const [syncSelectedItems, selected] = useSyncSelectedItems();
  const open = useCallback((sellerSku, internalItemId) => {
    sellerSkuRef.current = sellerSku;
    internalItemIdRef.current = internalItemId;
    setVisible(true);
  }, []);
  const close = useCallback(() => {
    clearAll();
    setVisible(false);
  }, [clearAll]);
  const onConfirm = () => {
    syncSelectedItems(sellerSkuRef.current);
    close();
  };
  const instance = visible && (
    <JustRightHeightModal
      visible={visible}
      onCancel={close}
      title={t('knowledge.item.sellerSku.same')}
      width={823}
      centered
      footer={
        <Space size={16}>
          <span className="tips">{t('storeSelector.已选{x}', { x: selected.length })}</span>
          <Button onClick={() => close()}>{t('phrase.取消')}</Button>
          <Button type="primary" onClick={onConfirm}>
            {t('department.确认选择')}
          </Button>
        </Space>
      }
      className={css`
        .ant-modal-body {
          padding: 0;
          margin: 0 !important;
        }
      `}
    >
      <Implement sellerSku={sellerSkuRef.current} internalItemId={internalItemIdRef.current} />
    </JustRightHeightModal>
  );

  return [open, instance] as const;
};

const Container = styled.div`
  max-height: calc(94vh - 108px);
  padding: 16px 16px 0 16px;
  display: flex;
  flex-direction: column;
  .items-select {
    max-height: calc(100% - 40px);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    .checkboxs {
      height: 44px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
    .list-container {
      max-height: calc(100% - 44px);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      .list {
        max-height: calc(100% - 44px);
        overflow: auto;
        .spin-container {
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
      .pagination {
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }
  }
`;

export default useSellerSkuSelect;
